import { useState } from 'react'
import { CaretRightOutlined } from '@ant-design/icons'
import type { BudgetMode, WorkflowJob } from '../../types'
import { formatShortDate, formatWorkflowHours, getWorkflowBadgeClass, JOB_STATUS_LABEL } from './workflowUtils'

interface Props {
  jobs: WorkflowJob[]
  budgetMode: BudgetMode
}

export function JobsListView({ jobs, budgetMode }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const dollarMode = budgetMode === 'dollars'

  function toggleRow(jobId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(jobId)) {
        next.delete(jobId)
      } else {
        next.add(jobId)
      }
      return next
    })
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Client</th>
            <th>Job</th>
            <th>Budget</th>
            <th>Used</th>
            <th>Progress</th>
            <th>Deadline</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const hasStaff = job.staff && job.staff.length > 0
            const isExpanded = expandedIds.has(job.jobId)
            return (
              <>
                <tr
                  key={job.jobId}
                  className={hasStaff ? 'expand-toggle' : undefined}
                  onClick={hasStaff ? () => toggleRow(job.jobId) : undefined}
                >
                  <td>
                    {hasStaff && (
                      <CaretRightOutlined
                        className={`expand-icon${isExpanded ? ' expanded' : ''}`}
                        aria-hidden="true"
                      />
                    )}
                    <strong>{job.clientName || 'Unassigned client'}</strong>
                  </td>
                  <td>
                    <div className="workflow-job-name">{job.jobName || 'Untitled job'}</div>
                    <div className="workflow-job-state">{job.state || 'Unknown'}</div>
                  </td>
                  <td>{formatWorkflowMetric(dollarMode ? job.budgetAmount : job.budgetHours, dollarMode)}</td>
                  <td>{formatWorkflowMetric(dollarMode ? job.usedAmount : job.usedHours, dollarMode)}</td>
                  <td>
                    <div className="progress-bar-wrap">
                      <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${Math.min(job.progressPct, 100)}%` }} />
                      </div>
                      <div className="progress-label">{Math.round(job.progressPct)}%</div>
                    </div>
                  </td>
                  <td>{formatShortDate(job.dueDate)}</td>
                  <td>
                    <span className={`badge ${getWorkflowBadgeClass(job.status)}`}>
                      {JOB_STATUS_LABEL[job.status]}
                    </span>
                  </td>
                </tr>
                {hasStaff && isExpanded && job.staff.map((member) => {
                  const base = dollarMode ? member.allocatedAmount : member.allocatedHours
                  const used = dollarMode ? member.usedAmount : member.usedHours
                  const pct = Number.isFinite(base) && base > 0
                    ? (used / base) * 100
                    : 0
                  return (
                    <tr key={`${job.jobId}-${member.staffId}`} className="expand-child">
                      <td>{member.staffName}</td>
                      <td />
                      <td>{formatWorkflowMetric(base, dollarMode)}</td>
                      <td>{formatWorkflowMetric(used, dollarMode)}</td>
                      <td>
                        {Number.isFinite(base) && base > 0 ? (
                          <div className="progress-bar-wrap">
                            <div className="progress-track">
                              <div className="progress-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <div className="progress-label">{Math.round(pct)}%</div>
                          </div>
                        ) : (
                          <div className="progress-label">0%</div>
                        )}
                      </td>
                      <td />
                      <td />
                    </tr>
                  )
                })}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function formatWorkflowMetric(value: number | null | undefined, dollarMode: boolean) {
  if (dollarMode) {
    if (value == null || !Number.isFinite(value)) return '-'
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(value)
  }

  return `${formatWorkflowHours(value)} hrs`
}
